import { useCallback, useEffect, useState } from "react";
import { Check, Landmark, LoaderCircle, ShieldCheck, X } from "lucide-react";
import { usePlaidLink, type PlaidLinkOnExitMetadata, type PlaidLinkOnSuccessMetadata } from "react-plaid-link";
import { createPlaidLinkToken, disconnectPlaid, exchangePlaidToken, getPlaidAccounts, reconnectPlaid, selectPlaidAccount, type PlaidCheckingAccount } from "../api";

interface Props { configured: boolean; connected: boolean; status?: string; pilotEnabled?: boolean; environment?: string; onConnected: () => Promise<void> }

function ConfiguredPlaidConnectButton({ connected, status, environment, onConnected }: Omit<Props, "configured">) {
  const needsReauth = status === "NEEDS_REAUTH";
  const selectionRequired = status === "ACCOUNT_SELECTION_REQUIRED";
  const oauthReturn = window.location.pathname === "/plaid/oauth";
  const [token, setToken] = useState<string | null>(() => oauthReturn ? sessionStorage.getItem("bni-plaid-link-token") : null);
  const [updateMode, setUpdateMode] = useState(() => oauthReturn && sessionStorage.getItem("bni-plaid-update-mode") === "true");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [accounts, setAccounts] = useState<PlaidCheckingAccount[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [showAccounts, setShowAccounts] = useState(false);
  const [autoLoadedSelection, setAutoLoadedSelection] = useState(false);
  const receiveAccounts = (choices: { accounts: PlaidCheckingAccount[] }) => {
    setAccounts(choices.accounts);
    setSelectedAccountId(choices.accounts.find((account) => account.selected)?.id ?? "");
    setShowAccounts(true);
  };
  const onSuccess = useCallback(async (publicToken: string, metadata: PlaidLinkOnSuccessMetadata) => {
    setLoading(true);
    setError("");
    try {
      const result = updateMode ? await reconnectPlaid() : await exchangePlaidToken(publicToken, metadata.accounts.map((account) => account.id));
      receiveAccounts(result);
      sessionStorage.removeItem("bni-plaid-link-token");
      sessionStorage.removeItem("bni-plaid-update-mode");
      if (oauthReturn) window.history.replaceState({}, "", "/");
      await onConnected();
    }
    catch (cause) { setError(cause instanceof Error ? cause.message : "We couldn’t finish connecting Chapter Checking."); }
    finally { setLoading(false); }
  }, [onConnected, oauthReturn, updateMode]);
  const onExit = useCallback((exitError: null | { display_message?: string | null }, _metadata: PlaidLinkOnExitMetadata) => {
    if (exitError) setError(exitError.display_message || "The bank connection was not completed. Please try again.");
  }, []);
  const { open, ready } = usePlaidLink({ token, onSuccess, onExit, ...(oauthReturn ? { receivedRedirectUri: window.location.href } : {}) });
  const connect = async () => {
    setLoading(true);
    setError("");
    try {
      const result = await createPlaidLinkToken();
      sessionStorage.setItem("bni-plaid-link-token", result.linkToken);
      sessionStorage.setItem("bni-plaid-update-mode", String(Boolean(result.updateMode)));
      setUpdateMode(Boolean(result.updateMode));
      setToken(result.linkToken);
    }
    catch (cause) { setError(cause instanceof Error ? cause.message : "We couldn’t start the secure bank connection."); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (token && ready) open(); }, [token, ready, open]);
  useEffect(() => {
    if (!selectionRequired || showAccounts || loading || autoLoadedSelection) return;
    setAutoLoadedSelection(true);
    setLoading(true);
    getPlaidAccounts().then(receiveAccounts).catch((cause) => setError(cause instanceof Error ? cause.message : "We couldn’t load the checking-account choices.")).finally(() => setLoading(false));
  }, [selectionRequired, showAccounts, loading, autoLoadedSelection]);
  const testMode = environment !== "production";
  const manage = async () => {
    setLoading(true);
    setError("");
    try { receiveAccounts(await getPlaidAccounts()); }
    catch (cause) { setError(cause instanceof Error ? cause.message : "We couldn’t load the checking-account choices."); }
    finally { setLoading(false); }
  };
  const saveSelection = async () => {
    if (!selectedAccountId) return;
    const previous = accounts.find((account) => account.selected)?.id;
    if (previous && previous !== selectedAccountId && !window.confirm("Change the Chapter checking account? Unreviewed activity imported from the previously selected account will be removed. Confirmed or closed-period activity will remain protected and be flagged for review.")) return;
    setLoading(true);
    setError("");
    try {
      await selectPlaidAccount(selectedAccountId);
      setShowAccounts(false);
      await onConnected();
    } catch (cause) { setError(cause instanceof Error ? cause.message : "We couldn’t save the Chapter checking account."); }
    finally { setLoading(false); }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect Chapter Checking? Transaction updates will stop and the Plaid subscription for this connection will end.")) return;
    setLoading(true);
    setError("");
    try { await disconnectPlaid(); await onConnected(); }
    catch (cause) { setError(cause instanceof Error ? cause.message : "We couldn’t disconnect Chapter Checking."); }
    finally { setLoading(false); }
  };
  return <div className="plaid-connect-control">
    <div className="plaid-connect-actions">
      {connected && !needsReauth ? <button className="secondary-button" onClick={() => void manage()} disabled={loading}>{loading ? <LoaderCircle className="spin" size={17} /> : <Landmark size={17} />}{selectionRequired ? "Choose Chapter Account" : "Manage Chapter Account"}</button> : <button className="secondary-button" onClick={() => void connect()} disabled={loading}>{loading ? <LoaderCircle className="spin" size={17} /> : <Landmark size={17} />}{needsReauth ? "Reconnect Chapter Checking" : testMode ? "Connect Test Bank" : "Connect Chapter Checking"}</button>}
      {connected ? <button className="text-button" onClick={() => void disconnect()} disabled={loading}>Disconnect</button> : null}
    </div>
    {selectionRequired ? <small className="plaid-connect-error" role="alert">Bank activity is paused until exactly one Chapter-owned checking account is confirmed.</small> : needsReauth ? <small>The bank needs a secure sign-in before updates can continue.</small> : connected ? <small>Only the selected Chapter checking account is imported. Personal accounts remain excluded.</small> : testMode ? <small>Test mode only—do not enter a real bank login.</small> : <small>Read-only: the platform can import activity but cannot move money.</small>}
    {error ? <small className="plaid-connect-error" role="alert">{error}</small> : null}
    {showAccounts ? <div className="plaid-account-dialog" role="dialog" aria-modal="true" aria-labelledby="plaid-account-title">
      <div className="plaid-account-card">
        <button className="icon-button plaid-account-close" onClick={() => setShowAccounts(false)} aria-label="Close account selection"><X /></button>
        <ShieldCheck className="plaid-account-shield" />
        <p className="eyebrow">Private Chapter banking</p>
        <h2 id="plaid-account-title">Choose the one Chapter checking account</h2>
        <p>Confirm only the account owned by the Chapter. Personal accounts will not sync, appear in the register, or be retained as active platform accounts.</p>
        <div className="plaid-account-options">
          {accounts.map((account) => <label className={selectedAccountId === account.id ? "plaid-account-option selected" : "plaid-account-option"} key={account.id}>
            <input type="radio" name="chapter-checking-account" value={account.id} checked={selectedAccountId === account.id} onChange={() => setSelectedAccountId(account.id)} />
            <span><strong>{account.name}</strong><small>{account.officialName && account.officialName !== account.name ? `${account.officialName} · ` : ""}ending in {account.mask ?? "unknown"}</small></span>
            {selectedAccountId === account.id ? <Check aria-hidden="true" /> : null}
          </label>)}
        </div>
        <div className="plaid-account-dialog-actions"><button className="secondary-button" onClick={() => void connect()} disabled={loading}>Change accounts available from bank</button><button className="primary-button" onClick={() => void saveSelection()} disabled={loading || !selectedAccountId}>{loading ? <LoaderCircle className="spin" /> : <Check />}Use this Chapter account</button></div>
        <small>If an account is missing, update the bank permissions above. For help identifying the Chapter-owned account, stop here and verify the last four digits with the Chapter’s bank records.</small>
      </div>
    </div> : null}
  </div>;
}

export function PlaidConnectButton({ configured, connected, status, pilotEnabled = true, environment, onConnected }: Props) {
  if (!configured) return <div className="plaid-connect-control plaid-not-configured"><button className="secondary-button" disabled><Landmark size={17} />Bank Connection Not Activated</button><small>No bank login was attempted. BNI Tampa Bay must complete secure Plaid provider setup before Chapter Checking can connect.</small></div>;
  if (!pilotEnabled && !connected) return <div className="plaid-connect-control plaid-not-configured"><button className="secondary-button" disabled><Landmark size={17} />Controlled Pilot</button><small>Chapter Checking will be enabled after this Chapter is approved for the secure banking pilot.</small></div>;
  return <ConfiguredPlaidConnectButton connected={connected} status={status} environment={environment} onConnected={onConnected} />;
}
